import classNames from 'classnames';
import type { CountryCode } from 'libphonenumber-js';
import { AnimatePresence, motion } from 'motion/react';
import type { FocusEvent } from 'react';
import { forwardRef, useMemo, useState } from 'react';

import { atoms } from '../../css/atoms/atoms.css';
import { Box } from '../Box/Box';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import { Adornment } from '../TextField/Adornment';
import { ClearButton } from '../TextField/ClearButton';
import type { TextFieldProps } from '../TextField/TextField';
import {
  cautionHoverStyle,
  criticalHoverStyle,
  defaultHoverStyle,
  fieldsetStyle,
  inputStyle,
  labelStyle,
  legendStyle,
  spanStyle,
  wrapperStyle,
} from '../TextField/TextField.css';
import { FlagSelector } from './components/FlagSelector';
import { adjustedLabelTransform } from './styles.css';

export interface PhoneFieldProps extends Omit<TextFieldProps, 'type'> {
  onCountryCodeChange: (countryCode: CountryCode) => void;
  showFlagSelector?: boolean;
  countryCode: CountryCode;
}

export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  (
    {
      id,
      value,
      onClear,
      onCountryCodeChange,
      onBlur,
      onFocus,
      label,
      labelIcon,
      message,
      tone,
      disabled,
      adornment,
      adornmentPosition = 'right',
      size = 'medium',
      readOnly = false,
      showFlagSelector,
      countryCode,
      ...restProps
    },
    forwardedRef,
  ) => {
    const [focused, setFocused] = useState(false);

    const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    const flagSelectorId = `${id}-flag-selector`;

    const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
      if (event?.relatedTarget?.id === flagSelectorId) {
        return;
      }

      setFocused(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const hasValue = typeof value === 'string' && value.length > 0;

    const hoverStyle = useMemo(() => {
      switch (true) {
        case focused || disabled:
          return undefined;
        case tone === 'critical':
          return criticalHoverStyle;
        case tone === 'caution':
          return cautionHoverStyle;
        default:
          return defaultHoverStyle;
      }
    }, [focused, disabled, tone]);

    const isLeftAdorned = adornmentPosition === 'left' && adornment !== undefined;

    const handleCountryCodeChange = (countryCode: CountryCode) => {
      onCountryCodeChange(countryCode);
    };

    const shouldAdjustLabelToDodgeFlagSelector = showFlagSelector && !focused && !hasValue;

    return (
      <Box display="flex" flexDirection="column">
        <div style={{ display: 'flex' }} className={wrapperStyle}>
          {label && (
            <label
              htmlFor={id}
              className={classNames(
                labelStyle({
                  size,
                  focused: focused || hasValue,
                  disabled,
                }),
                shouldAdjustLabelToDodgeFlagSelector ? adjustedLabelTransform : '',
              )}
              style={
                showFlagSelector && !focused && !hasValue
                  ? { transform: 'translate(7.5rem, 1.05rem) scale(1)' }
                  : undefined
              }
            >
              {label}
              {labelIcon}
            </label>
          )}
          <div
            style={{
              display: 'flex',
              flexGrow: 1,
            }}
            className={hoverStyle}
          >
            {showFlagSelector && (
              <FlagSelector
                id={flagSelectorId}
                countryCode={countryCode}
                onChange={handleCountryCodeChange}
                disabled={disabled}
              />
            )}
            <input
              id={id}
              value={value}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              className={inputStyle({
                size,
                isLeftAdorned,
              })}
              // type={type}
              disabled={disabled}
              ref={forwardedRef}
              readOnly={readOnly}
              {...restProps}
              type="tel"
            />
            <fieldset
              className={fieldsetStyle({
                focused,
                tone,
              })}
            >
              <legend
                className={legendStyle({
                  focused: focused || hasValue,
                })}
              >
                {label && (
                  <span className={spanStyle}>
                    {label}
                    {labelIcon}
                  </span>
                )}
              </legend>
            </fieldset>
          </div>

          {/* Clear Button */}
          <AnimatePresence mode="wait">
            {onClear && value && (
              <Adornment position="right">
                {
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ClearButton onClick={onClear} disabled={disabled} />
                  </motion.div>
                }
              </Adornment>
            )}
          </AnimatePresence>

          {/* End Adornment */}
          {!onClear && adornment && <Adornment position={adornmentPosition}>{adornment}</Adornment>}
        </div>
        {message && (
          <span
            className={atoms({
              paddingTop: 'xsmall',
            })}
          >
            <FieldMessage id={id} tone={tone} message={message} />
          </span>
        )}
      </Box>
    );
  },
);

PhoneField.displayName = 'PhoneField';
